/**
 * The suburb label shown in the directory's location pill (e.g. "Alexandria 2015").
 *
 * Extracted from pages/DirectoryPage.tsx so it can be unit-tested. It was inline, and
 * inline meant the O1 first-paint fix below had no way to be locked by a test — the
 * same reason applyRuntimeDirectoryRobots was extracted.
 *
 * DISPLAY ONLY. Never feed these values into a search key, a URL, or SEO metadata.
 */

/** Lowercase a label with any trailing 4-digit postcode stripped ("Alexandria 2015" → "alexandria"). */
export const stripPostcode = (s: string): string => s.replace(/\s+\d{4}\s*$/, '').trim().toLowerCase();

/**
 * Returns `label` only when it carries a postcode AND its suburb part EXACTLY matches
 * `suburb`. Rejects a prefix ("Newcastle West 2302" on the "Newcastle" page) and a stale
 * label left over from a previous suburb.
 */
export const matchedPostcodeDisplay = (label: string | undefined | null, suburb: string): string | undefined =>
    (!!label && /\b\d{4}\b/.test(label) && !!suburb && stripPostcode(label) === stripPostcode(suburb))
        ? label
        : undefined;

export interface LocationLabelSources {
    /** Suburb as displayed, already normalised ("Alexandria", never "Alexandria NSW 2015"). */
    cleanSuburb: string;
    /** Postcode the BUILD resolved for this route, carried in the SSG payload. */
    ssgPostcode?: string | undefined;
    /** Whether that payload belongs to THIS route. */
    isPrerendered: boolean;
    /** Backend's canonical display, available only after the search round-trip. */
    resolvedLocation?: string | undefined;
    /** Optimistic seed from an in-app navigation. */
    seededDisplay?: string | undefined;
}

/**
 * Resolve the pill label, preferring sources that are correct at the FIRST paint.
 *
 * O1 — the flick this closes. A visitor arriving from Google saw a bare "Adamstown"
 * and then, once the search returned, a swap to "Adamstown 2289". Neither pre-existing
 * source can help on a cold load: `resolvedLocation` only exists after the round-trip,
 * and `seededDisplay` only exists on an in-app navigation. The build already resolves
 * that postcode for the page title and now ships it in the payload, so it costs nothing.
 *
 * The SSG value is preferred OVER the live search result on a prerendered page. That is
 * deliberate: it is the register-backed value the <title> carries, so pill and title
 * always agree, and taking the search result instead would re-introduce a flick on the
 * 24 suburbs whose gym records disagree with the register — landing on the less
 * authoritative of the two.
 *
 * Every source still passes matchedPostcodeDisplay, so none of them can label a
 * different suburb than the page is about.
 */
export function resolveLocationLabel(src: LocationLabelSources): string {
    const { cleanSuburb, ssgPostcode, isPrerendered, resolvedLocation, seededDisplay } = src;
    if (!cleanSuburb) return cleanSuburb;

    const fromSsg = (isPrerendered && ssgPostcode) ? `${cleanSuburb} ${ssgPostcode}` : undefined;

    const label = matchedPostcodeDisplay(fromSsg, cleanSuburb)
        ?? matchedPostcodeDisplay(resolvedLocation, cleanSuburb)
        ?? matchedPostcodeDisplay(seededDisplay, cleanSuburb)
        ?? '';

    return label || cleanSuburb;
}
